import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { common, input } from "../i18n";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  AvailableSubOrganization,
  GoalInfo,
  Goals,
  YearEmissions,
} from "../models/GoalSetting";
import alertService from "../components/alert/alertService";
import { goalSettingService } from "../service/goal-setting.service";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

type GoalSettingState = {
  exitModal: () => Promise<void>;
  modalIsOpen: boolean;
  setModalIsOpen: Dispatch<SetStateAction<boolean>>;
  addGoalSettingForm: UseFormReturn<GoalSettingFormValue, any, GoalSettingFormValue>;
  selectionModel: GridRowSelectionModel;
  setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
  goals: Goals[];
  setGoals: Dispatch<SetStateAction<Goals[]>>;
  rows: Goals[];
  setRows: Dispatch<SetStateAction<Goals[]>>;
  subOrganizations: AvailableSubOrganization[];

  emissionOptions: YearEmissions[];
  setEmissionOptions: Dispatch<SetStateAction<YearEmissions[]>>;
  methodology: string | null;
  setMethodology: Dispatch<SetStateAction<string | null>>;
  isPlanFetched: boolean;
  setIsPlanFetched: Dispatch<SetStateAction<boolean>>;
  defaultBaseYear: number | null;
  setDefaultBaseYear: Dispatch<SetStateAction<number | null>>;

  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  goalInfo: GoalInfo | null;
  setGoalInfo: Dispatch<SetStateAction<GoalInfo | null>>;

  hasUserTriggered: boolean;
  setHasUserTriggered: Dispatch<SetStateAction<boolean>>;

  // baselineFormRef: React.MutableRefObject<UseFormReturn<any> | null>;
  // baseYearFormRef: React.MutableRefObject<UseFormReturn<any> | null>;
};

export type GoalSettingFormValue = {
  type: "organization" | "suborganization";
  suborganizationRef: string;
  name: string;
  standard: string;
  methodology: string;
  scope: string;
  unit: string;
};

export const Context = createContext<GoalSettingState>({} as GoalSettingState);

export const GoalSettingProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation([common, input], { nsMode: "fallback" });
  const navigate = useNavigate();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [goals, setGoals] = useState<Goals[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const [rows, setRows] = useState<Goals[]>([]);

  const [subOrganizations, setSubOrganizations] = useState<
    AvailableSubOrganization[]
  >([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [goalInfo, setGoalInfo] = useState<GoalInfo | null>(null);
  const [methodology, setMethodology] = useState<string | null>(null);

  const [emissionOptions, setEmissionOptions] = useState<YearEmissions[]>([]);

  const [isPlanFetched, setIsPlanFetched] = useState<boolean>(false);
  const [defaultBaseYear, setDefaultBaseYear] = useState<number | null>(null);
  const [hasUserTriggered, setHasUserTriggered] = useState(false);

  // const baselineFormRef = useRef<UseFormReturn<any> | null>(null);
  // const baseYearFormRef = useRef<UseFormReturn<any> | null>(null);

  const getAvailableSuborganizationList = async (): Promise<boolean> => {
    let result;
    try {
      result = await goalSettingService.getAvailableSuborganizationList();
    } catch {
      // error alert already shown by service
      return false;
    }
    if (result) {
      setSubOrganizations(result.suborganizations ?? []);
      const hasEmission = result.suborganizations.some(
        (s) => s.hasEmission === true,
      );

      if (!hasEmission) {
        await alertService.confirm(
          t("Historical-Data-Not-Found"),
          <Trans
            t={t}
            i18nKey="Historical-Data-At-Least-Description"
            components={{
              span: <span style={{ color: "var(--red)" }} />,
            }}
          />,
          t("Add-Emission"),
          "var(--dark)",
          true,
          undefined,
          undefined,
          undefined,
        );

        navigate("/emission/add");
        return false;
      }
      return true;
    }
    return false;
  };

  const getGoalSettingList = async () => {
    try {
      // alertService.showLoading();
      const result = await goalSettingService.getGoalSettingList();
      // alertService.closeLoading();

      if (result) {
        setGoals(result.goals);
      }
    } catch {
      // error alert already shown by service
    }
  };

  const exitModal = async () => {
    // ตรวจว่ามีการแก้ไขในฟอร์มหรือไม่
    const isDirty = addGoalSettingForm.formState.isDirty;

    // ถ้ายังไม่ได้แก้ไขอะไรเลย -> ปิดได้ทันที
    if (!isDirty) {
      addGoalSettingForm.reset({
        type: "organization",
        suborganizationRef: "",
        name: "",
        standard: "ghgProtocol",
        methodology: "",
        scope: "",
        unit: "tCO₂e",
      });

      setModalIsOpen(false);
      setIsEditMode(false);
      setGoalInfo(null);
      setMethodology(null);
      setEmissionOptions([]);
      setIsPlanFetched(false);
      return; // ออกจากฟังก์ชัน ไม่ต้องถาม
    }

    // ถ้ามีการแก้ไข ให้ถามยืนยันก่อน
    const isConfirmed = await alertService.selection(
      t("Unsaved-Changes"),
      t("Unsaved-Changes-Description"),
      t("Leave"),
      "#E5E7EB",
      true,
      "480px",
      t("Stay"),
      "var(--dark)",
    );

    if (isConfirmed) {
      addGoalSettingForm.reset({
        type: "organization",
        suborganizationRef: "",
        name: "",
        standard: "ghgProtocol",
        methodology: "",
        scope: "",
        unit: "tCO₂e",
      });

      setModalIsOpen(false);
      setIsEditMode(false);
      setGoalInfo(null);
      setMethodology(null);
      setEmissionOptions([]);
      setIsPlanFetched(false);
    }
  };

  const addGoalSettingForm = useForm<GoalSettingFormValue>({
    reValidateMode: "onChange",
    mode: "all",
  });

  useEffect(() => {
    const init = async () => {
      const hasEmission = await getAvailableSuborganizationList();
      if (hasEmission) {
        await getGoalSettingList();
      }
    };
    init();
  }, []);

  return (
    <Context.Provider
      value={{
        exitModal,
        modalIsOpen,
        setModalIsOpen,
        addGoalSettingForm,
        selectionModel,
        setSelectionModel,
        goals,
        setGoals,
        rows,
        setRows,
        subOrganizations,

        emissionOptions,
        setEmissionOptions,
        methodology,
        setMethodology,
        isPlanFetched,
        setIsPlanFetched,
        defaultBaseYear,
        setDefaultBaseYear,

        isEditMode,
        setIsEditMode,
        goalInfo,
        setGoalInfo,
        hasUserTriggered,
        setHasUserTriggered,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const GoalSettingContext = () => useContext(Context);
